import { Component, EventEmitter, Input, OnInit, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, Subscription } from 'rxjs';

import { removePermission } from '../../../../store/actions/access-permissions.actions';
import { selectUserInfo } from '../../../../store/selectors/account-permissions.selector';
import { selectRemoveUserStepperData } from '../../../../store/selectors/grant-revoke-permissions.selector';
import accountPermissionUtils from '../../../../utils/account-permission/account-permission.util';
import utils from '../../../../utils/account-permission/account-permission.util';
import { Unsubscribe } from '../../../../utils/decorators/dev';
import { AdobeTagging } from '../../../../utils/decorators/dev/adobe-launch';
import { RemoveUserStepBaseComponent } from '../remove-user-step-base.component';
import { adobeConfigData } from './raf-review-step-adobe';
import { AdobeAnalyticsProcessStep } from '../../../../services/adobe-launch/adobe-launch.process';
import { LoggerService } from '../../../../services/logger/logger.service';
import { ContentUtil } from 'src/app/utils/content/content.util';
import { UserDetailsDto } from 'src/common/dtos/user-details.dto';
import { AsyncPipe, NgFor } from '@angular/common';

@AdobeTagging(adobeConfigData)
@Unsubscribe
@Component({
    selector: 'zci-raf-review-step',
    templateUrl: './raf-review-step.component.html',
    styleUrls: ['./raf-review-step.component.scss'],
    imports: [
        AsyncPipe,
        NgFor,
    ],


})
export class RafReviewStepComponent extends RemoveUserStepBaseComponent implements OnInit {
    private readonly store = inject(Store);
    private readonly logger = inject(LoggerService);

    @Input() nextStepClicked: EventEmitter<any> = new EventEmitter<any>();
    @Input() nextStepReady: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() locationChanged: EventEmitter<any>;

    description = this.store.select(selectRemoveUserStepperData).pipe(
        filter(data => !!data.selectedUser),
        map(({ selectedUser }) => ContentUtil.grantorFragmentContent.remove_user.frmcRemoveUserReviewSubmit.body.replace(
            '${granteeName}', this.constructUserName(selectedUser)))
    );

    selectedAccounts = this.store.select(selectRemoveUserStepperData).pipe(
        filter(data => Array.isArray(data.selectedAccounts)),
        map(({ selectedAccounts }) => selectedAccounts)
    );

    subs = new Subscription();

    constructUserName: (user: UserDetailsDto) => string = accountPermissionUtils.constructUserName;

    ngOnInit(): void {
        this.nextStepClicked.subscribe(this.onNextStepLoad.bind(this));
    }

    onNextStepLoad() {
        this.subs.add(
            combineLatest([
                this.store.select(selectRemoveUserStepperData),
                this.store.select(selectUserInfo),
            ]).subscribe(([removeUserStepperData, grantorInfo]) => {
                this.logger.info(
                    `[RafReviewStep] Submitting remove access request. ` +
                    `Grantor POID: ${grantorInfo?.clientPoid}, ` +
                    `Grantee POID: ${removeUserStepperData?.selectedUser?.clientPoid}, ` +
                    `Number of accounts: ${removeUserStepperData?.selectedAccounts?.length ?? 0}`,
                );
                this.store.dispatch(
                    removePermission({
                        granteeDetails: {
                            granteeId: removeUserStepperData.selectedUser.clientPoid,
                            granteeName:
                                removeUserStepperData.selectedUser.firstName +
                                ' ' +
                                removeUserStepperData.selectedUser.lastName,
                            accessLevel: utils.getAccessLevel(
                                removeUserStepperData.selectedAccounts[0].grantees,
                                removeUserStepperData.selectedUser.clientPoid,
                                this.logger.warn,
                            ),
                        },
                        grantorDetails: {
                            accountId: removeUserStepperData.selectedAccounts[0].accountId,
                            grantorName: grantorInfo.firstName + ' ' + grantorInfo.lastName,
                            serviceAgreementId:
                                removeUserStepperData.selectedAccounts[0].serviceAgreementId,
                            isVBA: removeUserStepperData.selectedAccounts[0].isVBA,
                        },
                    }),
                );
                this.logger.info(
                    `[RafReviewStep] Dispatched removePermission. ` +
                    `Grantor POID: ${grantorInfo?.clientPoid}, ` +
                    `Grantee POID: ${removeUserStepperData?.selectedUser?.clientPoid}`,
                );
            }),
        );
    }

    protected readonly AdobeAnalyticsProcessStep = AdobeAnalyticsProcessStep;
}